import { useCallback, useEffect, useRef, useState } from "react";
import { styled } from "styled-components";

import { knowledgeApiSlice } from "api/knowledgeApi.slice";
import { ListItemBox, SectionTitleUnderlined, Spinner } from "common/components";
import { useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { getCurrentCustomMapName } from "domains/createMapDetail/selectors";
import { ExpandableKnowledgeItem } from "domains/publication/components/ExpandableKnowledgeItem";
import { LoadMoreButton } from "domains/topicDetail/components/LoadMoreButton";

type SimilarKnowledgeProps = { description: string; name: string; subtitle: string };

export const SimilarKnowledge = ({ description, name, subtitle }: SimilarKnowledgeProps) => {
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [fetchSimilarKnowledge, result] = knowledgeApiSlice.endpoints.getSimilarKnowledgeByDimension.useLazyQuery();
  const currentCustomMapName = useSelector(getCurrentCustomMapName);
  const [showingAllResults, setShowingAllResults] = useState<boolean>(false);
  const translate = useTranslate();

  const triggerFetchSimilarKnowledge = useCallback(() => {
    if (description.length === 0 && name.length === 0 && subtitle.length === 0) return;
    const body = { data: { description, name, parent_topic_name: currentCustomMapName, subtitle }, ignore_ids: null };
    void fetchSimilarKnowledge({ body, search: { limit: 30 } }, true);
  }, [currentCustomMapName, description, fetchSimilarKnowledge, name, subtitle]);

  useEffect(() => {
    if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);

    timeoutIdRef.current = setTimeout(() => {
      triggerFetchSimilarKnowledge();
      timeoutIdRef.current = null;
    }, 500);

    return () => {
      if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);
    };
  }, [triggerFetchSimilarKnowledge]);

  return (
    <>
      <StyledSectionTitleUnderlined>{translate("app.monitor.knowledge")}</StyledSectionTitleUnderlined>
      {result.isFetching && (
        <>
          <LoadingText>{translate("app.custom_key_issue.knowledge_preview.loading")}</LoadingText>
          <SpinnerContainer>
            <StyledSpinner />
          </SpinnerContainer>
        </>
      )}
      {result.isSuccess && !result.isFetching && (
        <>
          {(showingAllResults ? result.data : result.data.slice(0, KNOWLEDGE_LIMIT)).map((knowledge) => (
            <ListItemBox key={knowledge.id} $isExpandable={false}>
              <ExpandableKnowledgeItem
                id={knowledge.id}
                title={knowledge.title}
                summary={knowledge.summary}
                source={knowledge.source}
                timestamp={knowledge.time}
                signals={knowledge.signals ?? []}
                isBookmarked={!!knowledge.is_bookmarked}
                isPinned={!!knowledge.is_pinned}
                detailLink={knowledge.link}
                isExpandable={false}
              />
            </ListItemBox>
          ))}
          {result.data.length > KNOWLEDGE_LIMIT && (
            <LoadMoreButton showSeeMore={!showingAllResults} onClick={() => setShowingAllResults(!showingAllResults)} />
          )}
        </>
      )}
    </>
  );
};

const KNOWLEDGE_LIMIT = 7;

const StyledSectionTitleUnderlined = styled(SectionTitleUnderlined)`
  margin-top: 2rem;
`;

const SpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 1rem;
`;

const StyledSpinner = styled(Spinner)`
  color: ${({ theme }) => theme.colors.fillPrimary};
  height: 1.25rem;
  width: 1.25rem;
`;

const LoadingText = styled.p`
  color: ${({ theme }) => theme.colors.fillPrimary};
`;
